import PropTypes from 'prop-types';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import { superstyled, useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import { getAppSection } from '#client/utils/getAppSection';

import NoSSR from './noSSR';
import VeloAvatar from './VeloAvatar';

const StyledButton = superstyled({
  name: 'StyledButton',
  tag: Button,
  styles: () => ({
    padding: '0.25rem 1rem 0.25rem 0.5rem',
    fontWeight: 400,
    '&:hover': {
      border: '1px solid transparent',
    },
    border: '1px solid transparent',
  }),
});

const StyledIconOnlyButton = superstyled({
  name: 'StyledIconOnlyButton',
  tag: Button,
  styles: () => ({
    padding: '0.125rem',
    minWidth: 'auto',
    fontWeight: 400,
    '&:hover': {
      border: '1px solid transparent',
    },
    border: '1px solid transparent',
  }),
});

const GradientBox = superstyled({
  name: 'GradientBox',
  tag: Box,
  styles: () => ({
    borderRadius: '0.75rem',
    backgroundColor: '#FFFFFF',
    boxShadow: 'rgb(51 242 139 / 64%) -1px 2px 4px 0px, rgb(57 113 231 / 32%) 1px 4px 8px 0px',
  }),
});

const DEFAULT_PATH = '/guide/';
const COMPANY_PATH = '/guide/company/';

const VeloAssistantButton = ({
  clickstreamProps = {},
  url,
  variant = 'contained',
  color = 'primary',
  avatarColor,
  iconOnly = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { businessEntityUuid } = useBusinessEntity();

  /**
   * Prepare the navigation context for the Velo assistant button
   * This is used to store the navigation context in the session storage
   * so that it can be used to by velo to navigate back to the correct page and more in future
   * after the user has closed the chat
   * @returns {void}
   */
  const handleNavigationPrep = () => {
    const navigationContext = {
      referrer: window.location.pathname,
      businessEntityUuid,
      section: getAppSection(window.location.pathname) || 'unknown',
      timestamp: Date.now(),
      clickstreamContext: {
        elementName: clickstreamProps?.elementName || 'velo_assistant_button',
        elementPosition: clickstreamProps?.elementPosition || 'header',
      },
    };

    try {
      sessionStorage.setItem('velo_source_context', JSON.stringify(navigationContext));
    } catch (error) {
      console.warn('[Navigation] Failed to store context:', error);
    }
  };

  let path = DEFAULT_PATH;

  if (url) {
    // If url is provided, use it
    path = url;
  } else if (businessEntityUuid) {
    // If no url is provided, but we have a business entity uuid, use the company path
    path = `${COMPANY_PATH}${businessEntityUuid}/`;
  }

  const defaultAvatarColor = isMobile ? '#1e1e1e' : '#FAFAFA';
  const finalAvatarColor = avatarColor || defaultAvatarColor;

  const icon = (
    <NoSSR>
      <VeloAvatar state="default" color={finalAvatarColor} />
    </NoSSR>
  );

  const ButtonComponent = iconOnly ? StyledIconOnlyButton : StyledButton;

  return (
    <GradientBox>
      <ButtonComponent
        clickstreamProps={clickstreamProps}
        onClick={handleNavigationPrep}
        href={path}
        variant={variant}
        color={color}
        startIcon={iconOnly ? undefined : icon}
      >
        {iconOnly ? icon : <Text size="md">Ask Velo</Text>}
      </ButtonComponent>
    </GradientBox>
  );
};

VeloAssistantButton.propTypes = {
  clickstreamProps: PropTypes.shape({}),
  url: PropTypes.string,
  variant: PropTypes.string,
  color: PropTypes.string,
  avatarColor: PropTypes.string,
  iconOnly: PropTypes.bool,
};

export default VeloAssistantButton;
